import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import { useScrollLock } from "../hooks/useScrollLock";
import ErrorModal from "./ErrorModal";
import GuestCheckoutModal from "./GuestCheckoutModal";
import { useApi } from "../utils/Api";
import ItemModal from "./ItemModal";
import MobileCheckoutBar from "./MobileCheckoutBar";
import { checkStoreStatus } from "../utils/storeHours";

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const BLOCKED_DELIVERY_IDS = [9, 11, 12, 16];

const Basket = ({ isMobileCheckout }) => {
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const { cartItems, updateCartItemQuantity, removeItemFromCart } = useCart();
  const { user, openLoginModal } = useUser();

  const [isMobileBasketOpen, setIsMobileBasketOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutType, setCheckoutType] = useState("eat-in");
  const [editingCartItem, setEditingCartItem] = useState(null);

  const [showGuestCheckoutModal, setShowGuestCheckoutModal] = useState(false);
  const [pendingCheckoutType, setPendingCheckoutType] = useState(null);
  const [restaurantForcedOpen, setRestaurantForcedOpen] = useState(false);
  const [restaurantStatusLoaded, setRestaurantStatusLoaded] = useState(false);

  useScrollLock(isMobileBasketOpen);

  // Restaurant may be manually forced open by admin, overriding scheduled hours
  useEffect(() => {
    const fetchRestaurantStatus = async () => {
      try {
        const res = await apiFetch("restaurant-status");
        if (res?.success) {
          setRestaurantForcedOpen(Number(res.is_open) === 2);
        }
      } catch (err) {
        console.error("Failed to fetch restaurant status:", err);
      } finally {
        setRestaurantStatusLoaded(true);
      }
    };
    fetchRestaurantStatus();
  }, [apiFetch]);

  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const isDeliveryClosed = useCallback(() => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());

    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);

    return hour < 13 || hour > 21 || (hour === 21 && minute >= 30);
  }, []);

  const [deliveryClosed, setDeliveryClosed] = useState(isDeliveryClosed());

  useEffect(() => {
    const updateDeliveryStatus = () => {
      setDeliveryClosed(isDeliveryClosed());
    };

    updateDeliveryStatus();

    const interval = setInterval(updateDeliveryStatus, 60000);
    return () => clearInterval(interval);
  }, [isDeliveryClosed]);

  useEffect(() => {
    if (!user?.id || !pendingCheckoutType) return;

    navigate("/checkout", {
      state: { checkoutType: pendingCheckoutType },
    });

    setPendingCheckoutType(null);
  }, [user?.id, pendingCheckoutType, navigate]);

  const getItemTotals = (cartItem) => {
    if (!cartItem || !cartItem.item) return { total: 0, options: [] };

    let total = Number(cartItem.item.price || 0);
    const options = [];

    if (cartItem.selectedRequiredOption) {
      total += Number(cartItem.selectedRequiredOption.price || 0);
      options.push({
        name: cartItem.selectedRequiredOption.name,
        price: Number(cartItem.selectedRequiredOption.price || 0).toFixed(2),
      });
    }

    [
      ...(cartItem.item.swallowOptions || []),
      ...(cartItem.item.proteins || []),
    ].forEach((opt) => {
      const q = Number(opt.quantity) || 1;
      const optTotal = Number(opt.option_price || 0) * q;
      total += optTotal;

      options.push({
        name: opt.option_name + (q > 1 ? ` x${q}` : ""),
        price: optTotal.toFixed(2),
      });
    });

    return { total, options };
  };

  const subtotal = cartItems
    .reduce((acc, cartItem) => {
      const { total } = getItemTotals(cartItem);
      return acc + total * cartItem.quantity;
    }, 0);

  const grandTotal = subtotal.toFixed(2);

  const finalTotal = subtotal.toFixed(2);

  const checkStockBeforeCheckout = async () => {
    try {
      const data = await apiFetch("check-stock", { cartItems });

      if (!data || data.success !== true) {
        console.warn("Stock check unavailable — allowing checkout");
        return true;
      }

      if (data.soldOutItems?.length > 0) {
        const names = data.soldOutItems
          .map((item) => item.item_name)
          .join(", ");
        setErrorMessage(
          <>
            The following items are sold out: <b>{names}</b>.
            <br />
            Please remove them from your basket to proceed.
          </>,
        );
        return false;
      }

      return true;
    } catch (err) {
      console.warn("Stock check error — allowing checkout:", err);
      return true;
    }
  };

  const handleCheckout = async (type) => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);

    try {
      const { isEatInOpen, isTakeawayOpen } = checkStoreStatus();

      if (type === "eat-in" && restaurantStatusLoaded && !isEatInOpen && !restaurantForcedOpen) {
        setErrorMessage("Eat-in is currently closed. We reopen at 12:45 PM.");
        return;
      }

      if (type === "pickup" && restaurantStatusLoaded && !isTakeawayOpen && !restaurantForcedOpen) {
        const hasInvalidItems = cartItems.some(c => Number(c.item?.category_id) !== 6);
        if (hasInvalidItems) {
          setErrorMessage("Pick up is currently closed. You can only place orders for next-day collection items");
          return;
        }
      }

      if (type === "delivery") {
        if (deliveryClosed) {
          setErrorMessage("Delivery is only available from 1:00 PM to 9:30 PM UK time.");
          return;
        }

        const hasBlockedItem = cartItems.some((c) =>
          BLOCKED_DELIVERY_IDS.includes(Number(c.item?.category_id)),
        );

        if (hasBlockedItem) {
          setErrorMessage(
            "Alcoholic drinks cannot be delivered. Please remove them to proceed with delivery.",
          );
          return;
        }
      }

      if (!user?.id && (type === "pickup" || type === "delivery")) {
        setPendingCheckoutType(type);
        openLoginModal();
        return;
      }

      if (!user?.id && type === "eat-in") {
        setPendingCheckoutType("eat-in");
        setShowGuestCheckoutModal(true);
        return;
      }

      const canProceed = await checkStockBeforeCheckout();
      if (!canProceed) return;

      navigate("/checkout", { state: { checkoutType: type } });
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleMobileCheckout = async () => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);

    try {
      const canProceed = await checkStockBeforeCheckout();
      if (!canProceed) return;

      setIsMobileBasketOpen(false);
      navigate("/checkout");
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleGuestContinue = async () => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);

    try {
      const canProceed = await checkStockBeforeCheckout();
      if (!canProceed) return;

      setShowGuestCheckoutModal(false);

      navigate("/checkout", {
        state: {
          checkoutType: "eat-in",
          guestCheckout: true,
        },
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  const renderCartItems = () =>
    cartItems.map((cartItem) => {
      const { item, quantity, key } = cartItem;
      const { total, options } = getItemTotals(cartItem);
      const finalTotal = (total * quantity).toFixed(2);

      return (
        <li key={key} className="cart-item">
          <div className="cart-item-left">
            <div className="cart-item-header">
              <span className="cart-item-name">{item.item_name}</span>
              <span className="cart-item-price">
                £{Number(item.price || 0).toFixed(2)}
              </span>
            </div>

            {options.map((opt, idx) => (
              <div key={idx} className="cart-item-option">
                <span className="option-name">{opt.name}</span>
                <span className="option-price">£{opt.price}</span>
              </div>
            ))}

            <div className="cart-item-footer">
              <span className="final-total">£{finalTotal}</span>

              <div className="cart-controls">
                <button
                  className="qty-btn"
                  onClick={() =>
                    quantity > 1
                      ? updateCartItemQuantity(key, quantity - 1)
                      : removeItemFromCart(key)
                  }
                  disabled={isCheckingOut}
                >
                  −
                </button>

                <span>{quantity}</span>

                <button
                  className="qty-btn"
                  onClick={() => updateCartItemQuantity(key, quantity + 1)}
                  disabled={isCheckingOut}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="cart-item-right">
            <button
              className="edit-btn"
              onClick={() => setEditingCartItem(cartItem)}
              disabled={isCheckingOut}
            >
              <i className="fa fa-pen"></i>
            </button>

            <button
              className="delete-btn"
              onClick={() => removeItemFromCart(key)}
              disabled={isCheckingOut}
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        </li>
      );
    });

  const renderDesktopCheckoutBox = () => {
    const { isEatInOpen: isEatInOpenSchedule } = checkStoreStatus();
    const isEatInOpen =
      isEatInOpenSchedule || restaurantForcedOpen || !restaurantStatusLoaded;

    return (
    <div className="checkout-box-container">
      <div className="checkout-box">
        <div className="checkout-toggle-container">
          <div className="checkout-toggle">
            <button
              className={`${checkoutType === "eat-in" ? "active" : ""} ${!isEatInOpen ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => isEatInOpen && setCheckoutType("eat-in")}
              disabled={isCheckingOut || !isEatInOpen}
            >
              <i className="fa-solid fa-utensils"></i> Eat In
            </button>

            <button
              className={checkoutType === "pickup" ? "active" : ""}
              onClick={() => setCheckoutType("pickup")}
              disabled={isCheckingOut}
            >
              <i className="fa-solid fa-bag-shopping"></i> Pick Up
            </button>
{/* 
            <button
              className={checkoutType === "delivery" ? "active" : ""}
              onClick={() => setCheckoutType("delivery")}
              disabled={isCheckingOut}
            >
              <i className="fa-solid fa-truck"></i> Delivery
            </button> */}

          </div>
        </div>

        <div className="checkout-action">
          <button
            className="order-btn flex-space"
            onClick={() => handleCheckout(checkoutType)}
            disabled={isCheckingOut}
          >
            <span>Checkout</span>
            <span className="order-price">£{finalTotal}</span>
          </button>
        </div>
      </div>
    </div>
  );
  };

  return (
    <>
      {isCheckingOut && <SpinnerOverlay />}

      {!isMobileCheckout && (
        <aside className="basket-section desktop-basket">
          <div className="basket-header">
            <h3 className="basket-title">
              <span className="cart-icon">
                <i className="fa fa-shopping-cart"></i>
                {cartItems.length > 0 && (
                  <span className="cart-count">{cartCount}</span>
                )}
              </span>
              Basket
            </h3>
          </div>

          {cartItems.length === 0 ? (
            <p className="empty-basket">Your basket is empty</p>
          ) : (
            <ul className="basket-list">{renderCartItems()}</ul>
          )}

          {cartItems.length > 0 && renderDesktopCheckoutBox()}
        </aside>
      )}

      {isMobileCheckout && cartItems.length > 0 && (
        <>
          <MobileCheckoutBar
            cartCount={cartCount}
            grandTotal={finalTotal}
            onToggleBasket={() => setIsMobileBasketOpen((prev) => !prev)}
            isCheckingOut={isCheckingOut}
            isBasketOpen={isMobileBasketOpen}
          />

          {isMobileBasketOpen && (
            <div className="mobile-basket-container open">
              <div className="mobile-basket-header">
                <h3 className="basket-title font-bold">
                  <i className="fa fa-shopping-cart mr-1 text-[#fa9324]"></i>Basket
                </h3>
              </div>

              <ul className="basket-list">{renderCartItems()}</ul>

              <div className="mobile-basket-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>£{grandTotal}</span>
                </div>

                <div className="summary-row total-row border-t border-black/5 font-black text-lg">
                  <span>Total</span>
                  <span>£{finalTotal}</span>
                </div>
              </div>

              <button
                className={`add-to-cart-button mobile-checkout-btn ${isCheckingOut ? "opacity-70 cursor-not-allowed" : ""}`}
                onClick={handleMobileCheckout}
                disabled={isCheckingOut}
              >
                {isCheckingOut ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                    Processing...
                  </span>
                ) : (
                  <span>Checkout</span>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {showGuestCheckoutModal && (
        <GuestCheckoutModal
          isOpen={showGuestCheckoutModal}
          onClose={() => setShowGuestCheckoutModal(false)}
          onContinueGuest={handleGuestContinue}
          onLogin={() => {
            setShowGuestCheckoutModal(false);
            openLoginModal();
          }}
        />
      )}

      {errorMessage && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {editingCartItem && (
        <ItemModal
          isOpen={true}
          item={editingCartItem.item}
          cartItem={editingCartItem}
          onClose={() => setEditingCartItem(null)}
          mode={editingCartItem ? "edit" : "add"}
        />
      )}
    </>
  );
};

export default Basket;